import { Head } from '@inertiajs/react'
import { trackPanoramaEvent } from '@/frontend/lib/panoramaTracking'
import { BadgeCheck, Mail } from 'lucide-react'
import React, { useEffect, useRef, useState } from 'react'
import SurveyLayout from './components/SurveyLayout'

type PageComponent = React.FC<Props> & {
  layout?: (page: React.ReactElement) => React.ReactElement
}

interface PillarScores {
  ai: Record<string, number>
  social_commerce: Record<string, number>
  operacoes: Record<string, number>
}

interface Props {
  maturity_level: number
  maturity_score: number
  pillar_scores: PillarScores
  respondent_path: string
  email_captured: boolean
  level_name: string
  top_percentile: number | null
}

const DIMENSION_LABELS: Record<string, string> = {
  abrangencia: 'Abrangência',
  autonomia: 'Autonomia',
  integracao: 'Integração',
  governanca: 'Governança',
  cultura: 'Cultura',
  resultados: 'Resultados',
  conteudo_multicanal: 'Conteúdo Multicanal',
  consistencia: 'Consistência',
  live_commerce: 'Live Commerce',
  criadores_afiliados: 'Criadores/Afiliados',
  automacao_processos: 'Automação de Processos',
}

const PILLAR_LABELS: Record<string, string> = {
  ai: 'IA e Automação',
  social_commerce: 'Social Commerce',
  operacoes: 'Operações',
}

const LEVEL_DESCRIPTIONS: Record<number, string> = {
  1: 'Sua operação ainda não utiliza IA de forma estruturada. Há uma grande oportunidade de ganho de eficiência e competitividade ao iniciar a adoção.',
  2: 'Você já experimentou IA em algumas áreas, mas o uso ainda é pontual e sem governança. O próximo passo é estruturar e medir os resultados.',
  3: 'IA já faz parte do dia a dia em áreas específicas da sua operação. Para avançar, foque em integrar sistemas e expandir para mais áreas.',
  4: 'Sua operação tem IA conectando múltiplas áreas com supervisão humana. Você está entre os mais avançados do mercado brasileiro.',
  5: 'IA opera de forma autônoma em áreas definidas, com estratégia ligada a metas de negócio. Sua operação é referência no mercado.',
}

function percentileReaction(pct: number): { emoji: string; word: string } {
  if (pct <= 20) return { emoji: '🏆', word: 'Excepcional!' }
  if (pct <= 40) return { emoji: '🎉', word: 'Parabéns!' }
  if (pct <= 60) return { emoji: '🚀', word: 'Muito bem!' }
  if (pct <= 80) return { emoji: '👀', word: 'Boa!' }
  return { emoji: '💪', word: 'Vamos lá!' }
}

function celebrationEmojis(score: number): string[] {
  if (score >= 90) return ['🏆', '🎉', '🥇', '✨', '🔥', '💜']
  if (score >= 70) return ['🎉', '🚀', '⭐', '✨', '💪', '💜']
  if (score >= 50) return ['🚀', '👏', '💡', '✨', '🎯', '💜']
  if (score >= 30) return ['💡', '🌱', '👀', '🎯', '📈', '💜']
  return ['🌱', '💪', '🔑', '📈', '🚀', '💜']
}

function useBalloonCelebration(score: number) {
  useEffect(() => {
    const style = document.createElement('style')
    style.id = 'balloon-keyframes'
    style.textContent = `
      @keyframes balloon-float {
        0% { transform: translateY(0) scale(0.7); opacity: 0; }
        10% { opacity: 1; }
        75% { opacity: 1; }
        100% { transform: translateY(-110vh) scale(1); opacity: 0; }
      }
      @keyframes balloon-jiggle-0 {
        0%, 100% { translate: 0 0; rotate: -2deg; }
        50% { translate: 8px 0; rotate: 2deg; }
      }
      @keyframes balloon-jiggle-1 {
        0%, 100% { translate: 0 0; rotate: 1deg; }
        50% { translate: -6px 0; rotate: -2deg; }
      }
      @keyframes balloon-jiggle-2 {
        0%, 100% { translate: 4px 0; rotate: -1deg; }
        50% { translate: -4px 0; rotate: 1.5deg; }
      }
    `
    document.head.appendChild(style)

    const emojis = celebrationEmojis(score)
    const elements: HTMLDivElement[] = []
    const count = window.innerWidth < 768 ? 15 : 30

    for (let i = 0; i < count; i++) {
      const el = document.createElement('div')
      el.textContent = emojis[Math.floor(Math.random() * emojis.length)]
      const jiggle = `balloon-jiggle-${i % 3}`
      el.style.cssText = `
        position: fixed;
        font-size: ${80 + Math.random() * 60}px;
        left: ${Math.random() * 90}vw;
        bottom: -150px;
        z-index: 9999;
        pointer-events: none;
        animation: balloon-float ${3 + Math.random() * 2}s ease-out forwards, ${jiggle} ${0.8 + Math.random() * 0.6}s ease-in-out infinite;
        animation-delay: ${Math.random() * 0.8}s;
        opacity: 0;
      `
      document.body.appendChild(el)
      elements.push(el)
    }

    const timeout = setTimeout(() => {
      elements.forEach((el) => el.remove())
    }, 6000)

    return () => {
      clearTimeout(timeout)
      elements.forEach((el) => el.remove())
      style.remove()
    }
  }, [])
}

function useAnimatedCounter(target: number): number {
  const [value, setValue] = useState(0)
  const rafRef = useRef<number>(0)

  useEffect(() => {
    const duration = (target / 100) * 3000 // 3s for 100, proportional for lower
    const start = performance.now()

    const animate = (now: number) => {
      const elapsed = now - start
      const progress = Math.min(elapsed / duration, 1)
      // easeOut: decelerates toward the end
      const eased = 1 - Math.pow(1 - progress, 3)
      setValue(Math.round(eased * target))

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate)
      }
    }

    rafRef.current = requestAnimationFrame(animate)
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [target])

  return value
}

function scoreColor(score: number): string {
  if (score <= 25) return 'text-red-500'
  if (score <= 50) return 'text-orange-500'
  if (score <= 75) return 'text-amber-500'
  return 'text-emerald-500'
}

const Result: PageComponent = ({
  maturity_level,
  maturity_score,
  pillar_scores,
  respondent_path,
  email_captured,
  level_name,
  top_percentile,
}) => {
  useBalloonCelebration(maturity_score)

  useEffect(() => {
    if (localStorage.getItem('panorama_lead_fired')) return
    window.fbq?.('track', 'Lead', { content_name: 'Panorama E-commerce 2026', status: true })
    localStorage.setItem('panorama_lead_fired', '1')
  }, [])

  useEffect(() => {
    trackPanoramaEvent({
      eventName: 'panorama_result_view',
      respondentPath: respondent_path === 'ai_user' || respondent_path === 'non_ai_user' ? respondent_path : undefined,
      metadata: {
        email_captured,
        has_top_percentile: top_percentile !== null,
      },
    })
  }, [email_captured, respondent_path, top_percentile])

  const animatedScore = useAnimatedCounter(maturity_score)
  const [copied, setCopied] = useState(false)

  return (
    <>
      <Head title="Seu Resultado — Panorama E-commerce 2026" />

      <div className="py-8 md:py-16">
        {/* Header */}
        <div className="text-center mb-10">
          <p className="text-sm font-medium text-[#7c3aed] uppercase tracking-wider mb-2">
            Panorama de IA em E-commerces 2026
          </p>
          <h1
            className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-foreground mb-2"
            style={{ fontFamily: "'Instrument Serif', Georgia, serif" }}
          >
            Seu Índice de Maturidade
          </h1>
          <p className="text-slate-500 dark:text-muted-foreground">
            Baseado nas suas respostas, aqui está o diagnóstico da sua operação.
          </p>
        </div>

        {/* Unified Result Card */}
        <div className="bg-background rounded-xl border border-slate-200 dark:border-border p-6 md:p-8 mb-6 shadow-sm">
          {/* Top row: score + seal */}
          <div className="flex items-start justify-between mb-5">
            <div className="flex items-baseline gap-1">
              <span className={`text-5xl font-bold leading-none ${scoreColor(animatedScore)}`}>
                {animatedScore}
              </span>
              <span className="text-2xl font-medium text-slate-400 dark:text-muted-foreground leading-none">/100</span>
            </div>
            <BadgeCheck size={44} className="text-[#7c3aed]" strokeWidth={1.5} />
          </div>

          {/* Level title */}
          <h2 className="text-xl font-bold text-slate-900 dark:text-foreground mb-2">
            Nível {maturity_level}: {level_name}
          </h2>

          {/* Description */}
          <p className="text-slate-600 dark:text-muted-foreground text-sm leading-relaxed">
            {LEVEL_DESCRIPTIONS[maturity_level]}
          </p>

          {/* Pillar-grouped Dimension Scores */}
          <div className="mt-8 space-y-6">
            {(['ai', 'social_commerce', 'operacoes'] as const).map((pillarKey) => {
              const dimensions = pillar_scores[pillarKey]
              if (!dimensions || Object.keys(dimensions).length === 0) return null

              return (
                <div key={pillarKey}>
                  <h3 className="text-xs font-semibold text-slate-400 dark:text-muted-foreground uppercase tracking-wide mb-4">
                    {PILLAR_LABELS[pillarKey]}
                  </h3>
                  <div className="space-y-3">
                    {Object.entries(dimensions).map(([key, score]) => (
                      <div key={key}>
                        <div className="flex justify-between items-center mb-1.5">
                          <span className="text-sm font-medium text-slate-700 dark:text-muted-foreground">
                            {DIMENSION_LABELS[key] || key}
                          </span>
                          <span className="text-sm font-semibold text-slate-900 dark:text-foreground">{score}/5</span>
                        </div>
                        <div className="w-full bg-slate-100 dark:bg-accent rounded-full h-2.5">
                          <div
                            className="bg-[#7c3aed] h-2.5 rounded-full transition-all duration-500"
                            style={{ width: `${(score / 5) * 100}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Percentile — only shown with enough data */}
          {top_percentile !== null && (
            <p className="text-sm text-slate-500 dark:text-muted-foreground mt-6 pt-6 border-t border-slate-100 dark:border-border text-center">
              {percentileReaction(top_percentile).emoji}{' '}
              <span className="font-semibold text-slate-700 dark:text-muted-foreground">{percentileReaction(top_percentile).word}</span>{' '}
              Com esse resultado você ficou entre os{' '}
              <span className="font-semibold text-[#7c3aed]">top {top_percentile}%</span> com mais
              maturidade.
            </p>
          )}

        </div>

        {/* CTAs */}
        <div className="flex flex-col gap-3 mt-6 items-center">
          <button
            onClick={() => {
              trackPanoramaEvent({
                eventName: 'panorama_share_click',
                respondentPath: respondent_path === 'ai_user' || respondent_path === 'non_ai_user' ? respondent_path : undefined,
              })
              navigator.clipboard.writeText('https://stationzero.ai/panorama-ecommerce-2026')
              setCopied(true)
              setTimeout(() => setCopied(false), 2000)
            }}
            className="w-full max-w-xs px-6 py-2.5 bg-[#7c3aed] rounded-lg text-sm font-medium text-white hover:bg-[#6d28d9] transition-colors"
          >
            {copied ? 'Link copiado!' : 'Compartilhar pesquisa'}
          </button>
          <a
            href="https://stationzero.ai"
            onClick={() => {
              trackPanoramaEvent({
                eventName: 'panorama_outbound_click',
                respondentPath: respondent_path === 'ai_user' || respondent_path === 'non_ai_user' ? respondent_path : undefined,
                metadata: {
                  destination: 'stationzero',
                },
              })
            }}
            className="w-full max-w-xs px-6 py-2.5 bg-background border border-slate-200 dark:border-border rounded-lg text-sm font-medium text-slate-700 dark:text-muted-foreground hover:bg-slate-50 dark:hover:bg-accent transition-colors text-center"
          >
            Conhecer a StationZero
          </a>
          <a
            href="https://syncolive.com/"
            onClick={() => {
              trackPanoramaEvent({
                eventName: 'panorama_outbound_click',
                respondentPath: respondent_path === 'ai_user' || respondent_path === 'non_ai_user' ? respondent_path : undefined,
                metadata: {
                  destination: 'synco',
                },
              })
            }}
            className="w-full max-w-xs px-6 py-2.5 bg-background border border-slate-200 dark:border-border rounded-lg text-sm font-medium text-slate-700 dark:text-muted-foreground hover:bg-slate-50 dark:hover:bg-accent transition-colors text-center"
          >
            Conhecer a Synco
          </a>
        </div>

        {/* Email confirmation — separate card */}
        {email_captured && (
          <div className="bg-violet-50 dark:bg-violet-500/10 border border-violet-200 dark:border-violet-500/20 rounded-xl p-5 text-center mt-6">
            <p className="text-violet-800 dark:text-violet-400 text-sm inline-flex items-center gap-2 justify-center">
              <Mail size={16} className="flex-shrink-0" />
              Você receberá o relatório completo no seu e-mail quando estiver pronto.
            </p>
          </div>
        )}
      </div>
    </>
  )
}

Result.layout = (page: React.ReactElement) => <SurveyLayout>{page}</SurveyLayout>

export default Result
